import { useState, useRef } from 'react';

/**
 * ChipInput — edit a short list of string values as removable chips.
 *
 * Add by typing and pressing Enter (or comma); remove with the chip's × or by
 * pressing Backspace on an empty field. A pending draft is also committed when
 * focus leaves the whole control (so a typed-but-not-Entered value isn't lost
 * on Save) — but NOT when clicking a chip's × inside the control, which would
 * otherwise add an unwanted chip. Used for the Brand Brief's topic lists so the
 * edit view matches the read view.
 */
export function ChipInput({ value, onChange, placeholder, label }) {
  const items = value || [];
  const [draft, setDraft] = useState('');
  const containerRef = useRef(null);

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!items.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...items, v]);
    setDraft('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); }
    else if (e.key === 'Backspace' && !draft && items.length) onChange(items.slice(0, -1));
  };

  const onBlur = (e) => {
    if (draft.trim() && !containerRef.current?.contains(e.relatedTarget)) add();
  };

  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="chip-input" role="group" aria-label={label || placeholder || 'List'} ref={containerRef}>
      {items.map((t, i) => (
        <span key={t} className="chip-input-chip">
          {t}
          <button type="button" className="chip-input-x" aria-label={`Remove ${t}`} onClick={() => remove(i)}>×</button>
        </span>
      ))}
      <input
        className="chip-input-field"
        value={draft}
        placeholder={items.length ? 'Add another, press Enter' : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
      />
      <button type="button" className="chip-input-add" onClick={add} aria-label="Add">Add</button>
    </div>
  );
}
